import {
  portableBoardSnapshotV1Schema,
  type PortableBoardSnapshotV1,
} from "./portable-snapshot";
import {
  snapshotManifestV1Schema,
  type SnapshotManifestV1,
} from "./snapshot-manifest";

type CsvValue = boolean | number | string | null;

export type PortableStructuralFilePath =
  | "data/activity.csv"
  | "data/attachments.csv"
  | "data/board.json"
  | "data/cards.csv"
  | "data/checklists.csv"
  | "data/comments.csv"
  | "data/custom-fields.csv"
  | "data/labels.csv"
  | "data/lists.csv"
  | "data/members.csv"
  | "data/memberships.csv"
  | "index.html";

export type PortableArchiveTextFilePath =
  PortableStructuralFilePath | "manifest.json";

export interface PortableTextFile {
  contents: string;
  mediaType: string;
  path: PortableArchiveTextFilePath;
}

function protectCsvFormula(value: string): string {
  return /^(?:\s*[=+@-]|[\t\r\n])/u.test(value) ? `'${value}` : value;
}

function csvCell(value: CsvValue): string {
  const text = protectCsvFormula(value === null ? "" : String(value));
  return `"${text.replaceAll('"', '""')}"`;
}

function writeCsv(headers: readonly string[], rows: readonly CsvValue[][]) {
  return [headers, ...rows]
    .map((row) => row.map(csvCell).join(","))
    .join("\r\n")
    .concat("\r\n");
}

function writeBoardJson(snapshot: PortableBoardSnapshotV1): string {
  return `${JSON.stringify(snapshot, null, 2)}\n`;
}

function resolvedValues<T>(
  ids: readonly string[],
  valuesById: ReadonlyMap<string, T>,
  select: (value: T) => string | null,
): string {
  return ids
    .map((id) => {
      const value = valuesById.get(id);
      return value === undefined ? "" : (select(value) ?? "");
    })
    .join(";");
}

function writeListsCsv(snapshot: PortableBoardSnapshotV1): string {
  return writeCsv(
    [
      "list_id",
      "board_id",
      "board_name",
      "list_name",
      "list_status",
      "closed",
      "position",
    ],
    snapshot.lists.map((list) => [
      list.id,
      list.boardId,
      snapshot.board.name,
      list.name,
      list.closed ? "archived" : "open",
      list.closed,
      list.position,
    ]),
  );
}

function writeMembersCsv(snapshot: PortableBoardSnapshotV1): string {
  return writeCsv(
    ["member_id", "board_id", "board_name", "username", "full_name"],
    snapshot.members.map((member) => [
      member.id,
      snapshot.board.id,
      snapshot.board.name,
      member.username,
      member.fullName,
    ]),
  );
}

function writeMembershipsCsv(snapshot: PortableBoardSnapshotV1): string {
  const memberById = new Map(
    snapshot.members.map((member) => [member.id, member]),
  );

  return writeCsv(
    [
      "membership_id",
      "board_id",
      "board_name",
      "member_id",
      "username",
      "full_name",
      "member_type",
      "unconfirmed",
      "deactivated",
    ],
    snapshot.memberships.map((membership) => {
      const member =
        membership.memberId === null
          ? undefined
          : memberById.get(membership.memberId);
      return [
        membership.id,
        membership.boardId,
        snapshot.board.name,
        membership.memberId,
        member?.username ?? null,
        member?.fullName ?? null,
        membership.memberType,
        membership.unconfirmed,
        membership.deactivated,
      ];
    }),
  );
}

function writeLabelsCsv(snapshot: PortableBoardSnapshotV1): string {
  return writeCsv(
    ["label_id", "board_id", "board_name", "label_name", "color"],
    snapshot.labels.map((label) => [
      label.id,
      label.boardId,
      snapshot.board.name,
      label.name,
      label.color,
    ]),
  );
}

function writeCardsCsv(snapshot: PortableBoardSnapshotV1): string {
  const checklistById = new Map(
    snapshot.checklists.map((checklist) => [checklist.id, checklist]),
  );
  const labelById = new Map(snapshot.labels.map((label) => [label.id, label]));
  const listById = new Map(snapshot.lists.map((list) => [list.id, list]));
  const memberById = new Map(
    snapshot.members.map((member) => [member.id, member]),
  );

  return writeCsv(
    [
      "card_id",
      "card_name",
      "board_id",
      "board_name",
      "list_id",
      "list_name",
      "card_status",
      "list_status",
      "description",
      "closed",
      "position",
      "start",
      "due",
      "due_complete",
      "due_reminder_minutes",
      "date_last_activity",
      "card_number",
      "short_link",
      "short_url",
      "url",
      "cover_attachment_id",
      "manual_cover_attachment",
      "cover_color",
      "cover_size",
      "cover_brightness",
      "cover_uploaded_background_reference",
      "cover_is_template",
      "subscribed_by_exporting_member",
      "member_ids",
      "member_usernames",
      "member_names",
      "voter_member_ids",
      "voter_member_usernames",
      "voter_member_names",
      "label_ids",
      "label_names",
      "label_colors",
      "checklist_ids",
      "checklist_names",
    ],
    snapshot.cards.map((card) => {
      const list = listById.get(card.listId);
      return [
        card.id,
        card.name,
        card.boardId,
        snapshot.board.name,
        card.listId,
        list?.name ?? null,
        card.closed ? "archived" : "open",
        list === undefined ? null : list.closed ? "archived" : "open",
        card.description,
        card.closed,
        card.position,
        card.start,
        card.due,
        card.dueComplete,
        card.dueReminderMinutes,
        card.dateLastActivity,
        card.number,
        card.shortLink,
        card.shortUrl,
        card.url,
        card.coverAttachmentId,
        card.manualCoverAttachment,
        card.cover?.color ?? null,
        card.cover?.size ?? null,
        card.cover?.brightness ?? null,
        card.cover?.uploadedBackgroundReference ?? null,
        card.cover?.isTemplate ?? null,
        card.subscribed,
        card.memberIds.join(";"),
        resolvedValues(card.memberIds, memberById, ({ username }) => username),
        resolvedValues(card.memberIds, memberById, ({ fullName }) => fullName),
        card.voterMemberIds.join(";"),
        resolvedValues(
          card.voterMemberIds,
          memberById,
          ({ username }) => username,
        ),
        resolvedValues(
          card.voterMemberIds,
          memberById,
          ({ fullName }) => fullName,
        ),
        card.labelIds.join(";"),
        resolvedValues(card.labelIds, labelById, ({ name }) => name),
        resolvedValues(card.labelIds, labelById, ({ color }) => color),
        card.checklistIds.join(";"),
        resolvedValues(card.checklistIds, checklistById, ({ name }) => name),
      ];
    }),
  );
}

function writeChecklistsCsv(snapshot: PortableBoardSnapshotV1): string {
  const cardById = new Map(snapshot.cards.map((card) => [card.id, card]));
  const memberById = new Map(
    snapshot.members.map((member) => [member.id, member]),
  );
  const rows = snapshot.checklists.flatMap((checklist): CsvValue[][] => {
    const card = cardById.get(checklist.cardId);
    return [
      [
        "checklist",
        checklist.id,
        checklist.name,
        checklist.cardId,
        card?.name ?? null,
        checklist.position,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
      ],
      ...checklist.items.map((item): CsvValue[] => {
        const member =
          item.memberId === null ? undefined : memberById.get(item.memberId);
        return [
          "item",
          checklist.id,
          checklist.name,
          checklist.cardId,
          card?.name ?? null,
          checklist.position,
          item.id,
          item.name,
          item.state,
          item.position,
          item.due,
          item.memberId,
          member?.username ?? null,
          member?.fullName ?? null,
        ];
      }),
    ];
  });

  return writeCsv(
    [
      "row_type",
      "checklist_id",
      "checklist_name",
      "card_id",
      "card_name",
      "checklist_position",
      "check_item_id",
      "item_name",
      "item_state",
      "item_position",
      "item_due",
      "item_member_id",
      "item_member_username",
      "item_member_name",
    ],
    rows,
  );
}

function customFieldValue(value: {
  checked?: boolean;
  date?: string;
  number?: string;
  optionId?: string;
  text?: string;
  type: string;
}): string | null {
  if (value.checked !== undefined) {
    return String(value.checked);
  }
  if (value.date !== undefined) {
    return value.date;
  }
  if (value.number !== undefined) {
    return value.number;
  }
  if (value.optionId !== undefined) {
    return value.optionId;
  }
  return value.text ?? null;
}

function writeCustomFieldsCsv(snapshot: PortableBoardSnapshotV1): string {
  const fieldById = new Map(
    snapshot.customFields.map((field) => [field.id, field]),
  );
  const definitionRows = snapshot.customFields.flatMap(
    (field): CsvValue[][] => [
      [
        "field",
        field.id,
        field.name,
        field.type,
        field.displayOnCardFront,
        field.position,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
      ],
      ...field.options.map((option): CsvValue[] => [
        "option",
        field.id,
        field.name,
        field.type,
        field.displayOnCardFront,
        field.position,
        null,
        null,
        null,
        null,
        null,
        option.id,
        option.text,
        option.color,
        option.position,
      ]),
    ],
  );
  const valueRows = snapshot.cards.flatMap((card) =>
    card.customFieldItems.map((item): CsvValue[] => {
      const field = fieldById.get(item.customFieldId);
      const optionId =
        item.value.type === "option" ? item.value.optionId : null;
      const option =
        optionId === null
          ? undefined
          : field?.options.find(({ id }) => id === optionId);
      return [
        "value",
        item.customFieldId,
        field?.name ?? null,
        field?.type ?? null,
        field?.displayOnCardFront ?? null,
        field?.position ?? null,
        card.id,
        card.name,
        item.id,
        item.value.type,
        option?.text ?? customFieldValue(item.value),
        optionId,
        option?.text ?? null,
        option?.color ?? null,
        option?.position ?? null,
      ];
    }),
  );

  return writeCsv(
    [
      "row_type",
      "custom_field_id",
      "field_name",
      "field_type",
      "display_on_card_front",
      "field_position",
      "card_id",
      "card_name",
      "item_id",
      "value_type",
      "value",
      "option_id",
      "option_text",
      "option_color",
      "option_position",
    ],
    [...definitionRows, ...valueRows],
  );
}

function writeCommentsCsv(snapshot: PortableBoardSnapshotV1): string {
  const cardById = new Map(snapshot.cards.map((card) => [card.id, card]));
  return writeCsv(
    [
      "comment_id",
      "board_id",
      "board_name",
      "card_id",
      "card_name",
      "author_member_id",
      "author_username",
      "author_name",
      "created_at",
      "text",
    ],
    snapshot.comments.map((comment) => [
      comment.id,
      comment.boardId,
      snapshot.board.name,
      comment.cardId,
      comment.cardName ?? cardById.get(comment.cardId)?.name ?? null,
      comment.author.id,
      comment.author.username,
      comment.author.fullName,
      comment.createdAt,
      comment.text,
    ]),
  );
}

function writeActivityCsv(snapshot: PortableBoardSnapshotV1): string {
  const cardById = new Map(snapshot.cards.map((card) => [card.id, card]));

  return writeCsv(
    [
      "action_id",
      "board_id",
      "board_name",
      "card_id",
      "card_name_at_action",
      "current_card_name",
      "card_currently_present",
      "action_type",
      "capture_sources",
      "actor_member_id",
      "actor_username",
      "actor_name",
      "created_at",
      "comment_text",
      "source_data_json",
      "display_json",
    ],
    snapshot.activities.map((activity) => {
      const currentCard = cardById.get(activity.cardId);
      const text = activity.data.text;
      return [
        activity.id,
        activity.boardId,
        snapshot.board.name,
        activity.cardId,
        activity.cardName,
        currentCard?.name ?? null,
        currentCard !== undefined,
        activity.type,
        (activity.sources ?? ["trello_historical_read"]).join("|"),
        activity.actor.id,
        activity.actor.username,
        activity.actor.fullName,
        activity.createdAt,
        typeof text === "string" ? text : null,
        JSON.stringify(activity.data),
        activity.display === null ? null : JSON.stringify(activity.display),
      ];
    }),
  );
}

function writeAttachmentsCsv(
  snapshot: PortableBoardSnapshotV1,
  manifest: SnapshotManifestV1 | null,
): string {
  const cardById = new Map(snapshot.cards.map((card) => [card.id, card]));
  const outcomeById = new Map(
    (manifest?.attachments ?? []).map((outcome) => [
      outcome.attachmentId,
      outcome,
    ]),
  );

  return writeCsv(
    [
      "attachment_id",
      "board_id",
      "board_name",
      "card_id",
      "card_name",
      "attachment_name",
      "source_file_name",
      "url",
      "is_upload",
      "mime_type",
      "byte_size",
      "attached_at",
      "source_type",
      "disposition",
      "planned_archive_path",
      "classification_warning_code",
      "is_card_cover",
      "capture_status",
      "archive_path",
      "captured_byte_size",
      "sha256",
      "capture_warning_code",
    ],
    snapshot.attachments.map((attachment) => {
      const outcome = outcomeById.get(attachment.id);
      return [
        attachment.id,
        snapshot.board.id,
        snapshot.board.name,
        attachment.cardId,
        cardById.get(attachment.cardId)?.name ?? null,
        attachment.name,
        attachment.sourceFileName,
        attachment.url,
        attachment.isUpload,
        attachment.mimeType,
        attachment.byteSize,
        attachment.attachedAt,
        attachment.sourceType,
        attachment.disposition,
        manifest === null || outcome?.status === "copied"
          ? attachment.plannedArchivePath
          : null,
        attachment.classificationWarningCode,
        cardById.get(attachment.cardId)?.coverAttachmentId === attachment.id,
        outcome?.status ?? null,
        outcome?.archivePath ?? null,
        outcome?.byteSize ?? null,
        outcome?.sha256 ?? null,
        outcome?.warningCode ?? null,
      ];
    }),
  );
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function textBlock(value: string): string {
  return escapeHtml(value).replaceAll("\n", "<br>");
}

function safeHttpsHref(value: string | null): string | null {
  if (value === null) {
    return null;
  }

  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      url.username === "" &&
      url.password === ""
      ? value
      : null;
  } catch {
    return null;
  }
}

function renderEmpty(message: string): string {
  return `<p class="empty">${escapeHtml(message)}</p>`;
}

function renderIndexHtml(
  snapshot: PortableBoardSnapshotV1,
  manifest: SnapshotManifestV1 | null,
): string {
  const cardsByList = new Map(
    snapshot.lists.map((list) => [
      list.id,
      snapshot.cards.filter((card) => card.listId === list.id),
    ]),
  );
  const checklistById = new Map(
    snapshot.checklists.map((checklist) => [checklist.id, checklist]),
  );
  const customFieldById = new Map(
    snapshot.customFields.map((field) => [field.id, field]),
  );
  const labelById = new Map(snapshot.labels.map((label) => [label.id, label]));
  const memberById = new Map(
    snapshot.members.map((member) => [member.id, member]),
  );
  const renderBoardMemberships = (): string => {
    if (snapshot.memberships.length === 0) {
      return "";
    }

    const items = snapshot.memberships
      .map((membership) => {
        const member =
          membership.memberId === null
            ? undefined
            : memberById.get(membership.memberId);
        const name =
          member?.fullName ?? membership.memberId ?? "Unconfirmed member";
        const role = membership.memberType ?? "role unavailable";
        const states = [
          membership.unconfirmed === true ? "unconfirmed" : null,
          membership.deactivated === true ? "deactivated" : null,
        ].filter((value): value is string => value !== null);
        return `<li><strong>${escapeHtml(name)}</strong><span>${escapeHtml(role)}${states.length === 0 ? "" : ` · ${escapeHtml(states.join(" · "))}`}</span></li>`;
      })
      .join("");
    return `<section class="board-access"><h2>Board access at capture</h2><ul>${items}</ul></section>`;
  };
  const commentsByCard = new Map(
    snapshot.cards.map((card) => [
      card.id,
      snapshot.comments.filter((comment) => comment.cardId === card.id),
    ]),
  );
  const activitiesByCard = new Map(
    snapshot.cards.map((card) => [
      card.id,
      snapshot.activities.filter((activity) => activity.cardId === card.id),
    ]),
  );
  const currentCardIds = new Set(snapshot.cards.map(({ id }) => id));
  const historicalCardActivities = snapshot.activities.filter(
    (activity) => !currentCardIds.has(activity.cardId),
  );
  const attachmentsByCard = new Map(
    snapshot.cards.map((card) => [
      card.id,
      snapshot.attachments.filter(
        (attachment) => attachment.cardId === card.id,
      ),
    ]),
  );
  const attachmentOutcomeById = new Map(
    (manifest?.attachments ?? []).map((outcome) => [
      outcome.attachmentId,
      outcome,
    ]),
  );

  const renderChecklist = (checklistId: string): string => {
    const checklist = checklistById.get(checklistId);
    if (checklist === undefined) {
      return "";
    }

    const items = checklist.items
      .map(
        (item) =>
          `<li><span aria-hidden="true">${
            item.state === "complete" ? "☑" : "☐"
          }</span> ${textBlock(item.name)}</li>`,
      )
      .join("");
    return `<section class="detail"><h4>${escapeHtml(checklist.name)}</h4><ul class="checklist">${items}</ul></section>`;
  };

  const renderCustomFields = (
    card: PortableBoardSnapshotV1["cards"][number],
  ): string => {
    if (card.customFieldItems.length === 0) {
      return "";
    }

    const items = card.customFieldItems
      .map((item) => {
        const field = customFieldById.get(item.customFieldId);
        const fieldName = field?.name ?? item.customFieldId;
        let value = customFieldValue(item.value) ?? "Unavailable";

        if (item.value.type === "option") {
          const optionId = item.value.optionId;
          value =
            field?.options.find(({ id }) => id === optionId)?.text ?? optionId;
        }

        return `<li><strong>${escapeHtml(fieldName)}:</strong> ${escapeHtml(value)}</li>`;
      })
      .join("");
    return `<section class="detail"><h4>Custom fields</h4><ul>${items}</ul></section>`;
  };

  const renderComments = (cardId: string): string => {
    const comments = commentsByCard.get(cardId) ?? [];
    if (comments.length === 0) {
      return "";
    }

    return `<section class="detail"><h4>Comments</h4>${comments
      .map(
        (comment) =>
          `<blockquote><p>${textBlock(comment.text)}</p><footer>${escapeHtml(comment.author.fullName)} · <time datetime="${escapeHtml(comment.createdAt)}">${escapeHtml(comment.createdAt)}</time></footer></blockquote>`,
      )
      .join("")}</section>`;
  };

  const humanizeActionType = (type: string): string =>
    type
      .replace(/([a-z0-9])([A-Z])/gu, "$1 $2")
      .replace(/^./u, (character) => character.toUpperCase());

  const renderActivityItems = (
    activities: PortableBoardSnapshotV1["activities"],
  ): string =>
    activities
      .map((activity) => {
        const text = activity.data.text;
        const comment =
          typeof text === "string" && text !== ""
            ? `<p>${textBlock(text)}</p>`
            : "";
        const sources = (activity.sources ?? ["trello_historical_read"])
          .map((source) =>
            source === "trello_webhook_carry_forward"
              ? "signed webhook carry-forward"
              : "Trello historical read",
          )
          .join(" + ");
        return `<li><strong>${escapeHtml(humanizeActionType(activity.type))}</strong><span>${escapeHtml(activity.actor.fullName)} · <time datetime="${escapeHtml(activity.createdAt)}">${escapeHtml(activity.createdAt)}</time> · ${escapeHtml(sources)}</span>${comment}<details><summary>Source details</summary><pre>${escapeHtml(JSON.stringify(activity.data, null, 2))}</pre></details></li>`;
      })
      .join("");

  const renderActivities = (cardId: string): string => {
    const activities = activitiesByCard.get(cardId) ?? [];
    return activities.length === 0
      ? ""
      : `<section class="detail"><h4>Card activity</h4><ol class="activity">${renderActivityItems(activities)}</ol></section>`;
  };

  const renderAttachments = (cardId: string): string => {
    const attachments = attachmentsByCard.get(cardId) ?? [];
    if (attachments.length === 0) {
      return "";
    }

    const items = attachments
      .map((attachment) => {
        const outcome = attachmentOutcomeById.get(attachment.id);
        const href =
          outcome?.status === "copied"
            ? outcome.archivePath
            : safeHttpsHref(attachment.url);
        const name = escapeHtml(attachment.name);
        const renderedName =
          href === null
            ? name
            : `<a href="${escapeHtml(href)}" rel="noreferrer noopener">${name}</a>`;
        return `<li>${renderedName}<span>${escapeHtml(attachment.sourceType)} · ${escapeHtml(outcome?.status ?? attachment.disposition)}${outcome?.warningCode ? ` · ${escapeHtml(outcome.warningCode)}` : ""}</span></li>`;
      })
      .join("");
    return `<section class="detail"><h4>Attachments</h4><ul class="attachments">${items}</ul></section>`;
  };

  const renderCard = (
    card: PortableBoardSnapshotV1["cards"][number],
  ): string => {
    const labels = card.labelIds
      .map((id) => labelById.get(id)?.name || labelById.get(id)?.color || id)
      .map((label) => `<span class="tag">${escapeHtml(label)}</span>`)
      .join("");
    const members = card.memberIds
      .map((id) => memberById.get(id)?.fullName ?? id)
      .join(", ");
    const dates = [
      card.start === null ? null : `Start: ${card.start}`,
      card.due === null
        ? null
        : `Due: ${card.due}${card.dueComplete ? " · complete" : ""}`,
    ].filter((value): value is string => value !== null);
    const cover =
      card.cover === null
        ? null
        : [
            card.cover.color === null
              ? "Image cover"
              : `Colour cover: ${card.cover.color}`,
            `size: ${card.cover.size}`,
            `brightness: ${card.cover.brightness}`,
          ].join(" · ");

    return `<article class="card${card.closed ? " archived" : ""}">
      <header><h3>${escapeHtml(card.name)}</h3>${card.closed ? '<span class="archived-label">Archived</span>' : ""}</header>
      ${labels === "" ? "" : `<div class="tags">${labels}</div>`}
      ${card.description === "" ? "" : `<p>${textBlock(card.description)}</p>`}
      ${members === "" ? "" : `<p class="meta"><strong>Members:</strong> ${escapeHtml(members)}</p>`}
      ${dates.length === 0 ? "" : `<p class="meta">${dates.map(escapeHtml).join(" · ")}</p>`}
      ${cover === null ? "" : `<p class="meta"><strong>Card cover:</strong> ${escapeHtml(cover)}</p>`}
      ${card.checklistIds.map(renderChecklist).join("")}
      ${renderCustomFields(card)}
      ${renderActivities(card.id)}
      ${renderComments(card.id)}
      ${renderAttachments(card.id)}
    </article>`;
  };

  const lists = snapshot.lists
    .map((list) => {
      const cards = cardsByList.get(list.id) ?? [];
      return `<section class="list${list.closed ? " archived" : ""}">
        <header><h2>${escapeHtml(list.name)}</h2>${list.closed ? '<span class="archived-label">Archived</span>' : `<span>${cards.length} cards</span>`}</header>
        ${cards.length === 0 ? renderEmpty("No cards captured in this list.") : cards.map(renderCard).join("")}
      </section>`;
    })
    .join("");

  const historicalActivity =
    historicalCardActivities.length === 0
      ? ""
      : `<section class="list archived"><header><h2>Historical activity for cards no longer present</h2><span>${historicalCardActivities.length} actions</span></header><ol class="activity">${renderActivityItems(historicalCardActivities)}</ol></section>`;
  const webhookCoverage = snapshot.activityCoverage?.webhookCarryForward;
  const activityCoverageNotice =
    webhookCoverage === undefined
      ? ""
      : ` Signed-webhook coverage starts after activation at <time datetime="${escapeHtml(webhookCoverage.activatedAt)}">${escapeHtml(webhookCoverage.activatedAt)}</time> and remains best effort: this snapshot considered ${webhookCoverage.eligibleStoredActionCount} stored webhook action${webhookCoverage.eligibleStoredActionCount === 1 ? "" : "s"}, merged ${webhookCoverage.mergedActionCount}, and carried ${webhookCoverage.carriedForwardActionCount} webhook-sourced action${webhookCoverage.carriedForwardActionCount === 1 ? "" : "s"} from ${webhookCoverage.carriedForwardFromSnapshotId === null ? "no prior snapshot" : `prior snapshot ${escapeHtml(webhookCoverage.carriedForwardFromSnapshotId)}`}. It cannot backfill activity from before activation or replay original actors and timestamps in Trello.`;

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta name="generator" content="Board Preserve">
  <title>${escapeHtml(snapshot.board.name)} · Board Preserve</title>
  <style>
    :root{color:#172b4d;background:#edf1f6;font:16px/1.55 system-ui,-apple-system,"Segoe UI",sans-serif}*{box-sizing:border-box}body{margin:0}main{width:min(980px,100%);margin:auto;padding:32px 20px 64px}.hero{padding:28px;border-radius:16px;background:#10243e;color:white}.hero p{max-width:70ch;margin:12px 0 0;color:#cfdbeb}.summary{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;margin-top:20px;overflow:hidden;border-radius:10px;background:#314b6c}.summary div{padding:12px;background:#1b3659}.summary strong,.summary span{display:block}.summary span{color:#c4d2e4;font-size:.75rem}.notice{margin:20px 0;padding:14px 16px;border:1px solid #c8d7eb;border-radius:10px;background:#f7faff;color:#44546f}.board-access{margin:20px 0;padding:16px 20px;border:1px solid #d8dee9;border-radius:12px;background:white}.board-access h2{margin:0;font-size:1rem}.board-access ul{display:flex;flex-wrap:wrap;gap:8px;margin:10px 0 0;padding:0;list-style:none}.board-access li{padding:7px 10px;border-radius:8px;background:#f1f2f4}.board-access span{display:block;color:#6b778c;font-size:.72rem}.list{margin-top:20px;padding:20px;border:1px solid #d8dee9;border-radius:12px;background:white}.list>header,.card>header{display:flex;align-items:center;justify-content:space-between;gap:12px}.list h2,.card h3,.detail h4{margin:0}.list>header>span{color:#6b778c;font-size:.8rem}.card{margin-top:14px;padding:18px;border:1px solid #e1e5eb;border-radius:9px}.card p{white-space:normal}.tags{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px}.tag,.archived-label{padding:3px 7px;border-radius:999px;background:#e9f2ff;color:#0c4a9c;font-size:.7rem;font-weight:700}.archived{border-style:dashed}.archived-label{background:#f1f2f4;color:#5e6c84}.meta{color:#5e6c84;font-size:.82rem}.detail{margin-top:14px;padding-top:12px;border-top:1px solid #e7eaf0}.detail h4{font-size:.78rem;text-transform:uppercase;letter-spacing:.05em;color:#5e6c84}.detail ul{margin:8px 0 0;padding-left:20px}.checklist{list-style:none;padding:0!important}.attachments li{margin-bottom:6px}.attachments span,.activity span{display:block;color:#6b778c;font-size:.72rem}.activity{margin:8px 0 0;padding-left:22px}.activity li{margin-bottom:10px}.activity p{margin:4px 0}.activity details{margin-top:4px;color:#5e6c84;font-size:.72rem}.activity pre{overflow:auto;padding:8px;background:#f7f8f9;white-space:pre-wrap}blockquote{margin:10px 0;padding:10px 12px;border-left:3px solid #85b8ff;background:#f7faff}blockquote p{margin:0}blockquote footer{margin-top:6px;color:#6b778c;font-size:.72rem}.empty{color:#6b778c;font-style:italic}a{color:#0c66e4;overflow-wrap:anywhere}@media(max-width:620px){main{padding:0 0 40px}.hero,.list{border-radius:0}.summary{grid-template-columns:repeat(2,1fr)}}@media print{:root{background:white}main{width:100%;padding:0}.hero{color:#172b4d;background:white;border:1px solid #d8dee9}.hero p{color:#44546f}.summary{background:#d8dee9}.summary div{background:white}.summary span{color:#5e6c84}.list,.card{break-inside:avoid;box-shadow:none}}
  </style>
</head>
<body>
  <main>
    <header class="hero">
      <h1>${escapeHtml(snapshot.board.name)}</h1>
      ${snapshot.board.description === "" ? "" : `<p>${textBlock(snapshot.board.description)}</p>`}
      <div class="summary" aria-label="Snapshot summary">
        <div><strong>${snapshot.lists.length}</strong><span>Lists</span></div>
        <div><strong>${snapshot.cards.length}</strong><span>Cards</span></div>
        <div><strong>${snapshot.activities.length}</strong><span>Card activity</span></div>
        <div><strong>${snapshot.attachments.length}</strong><span>Attachments</span></div>
      </div>
    </header>
    <p class="notice">Captured by Board Preserve at <time datetime="${escapeHtml(snapshot.capturedAt)}">${escapeHtml(snapshot.capturedAt)}</time>.${manifest === null ? "" : ` Archive status: ${escapeHtml(manifest.status)} with ${manifest.warnings.length} warning${manifest.warnings.length === 1 ? "" : "s"}.`} Trello source IDs are retained for reference. Card activity and comments are archive-only and cannot be recreated with their original actors or timestamps. Trello omits some action types from historical nested-action responses.${activityCoverageNotice} ${manifest === null ? "Attachment files remain source links in Local Export." : "Attachment outcomes and copied-file paths are recorded in the manifest."}</p>
    ${renderBoardMemberships()}
    ${lists === "" ? renderEmpty("No lists were captured.") : lists}
    ${historicalActivity}
  </main>
</body>
</html>
`;
}

export function buildPortableStructuralFiles(
  snapshotInput: PortableBoardSnapshotV1,
  manifestInput?: SnapshotManifestV1,
): readonly PortableTextFile[] {
  const snapshot = portableBoardSnapshotV1Schema.parse(snapshotInput);
  const manifest =
    manifestInput === undefined
      ? null
      : snapshotManifestV1Schema.parse(manifestInput);

  if (
    manifest !== null &&
    (manifest.snapshotId !== snapshot.snapshotId ||
      manifest.capturedAt !== snapshot.capturedAt ||
      manifest.source.boardId !== snapshot.source.boardId)
  ) {
    throw new RangeError("Manifest metadata must match the portable snapshot.");
  }

  return [
    {
      contents: renderIndexHtml(snapshot, manifest),
      mediaType: "text/html;charset=utf-8",
      path: "index.html",
    },
    {
      contents: writeBoardJson(snapshot),
      mediaType: "application/json;charset=utf-8",
      path: "data/board.json",
    },
    {
      contents: writeListsCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/lists.csv",
    },
    {
      contents: writeMembersCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/members.csv",
    },
    {
      contents: writeMembershipsCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/memberships.csv",
    },
    {
      contents: writeLabelsCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/labels.csv",
    },
    {
      contents: writeCardsCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/cards.csv",
    },
    {
      contents: writeChecklistsCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/checklists.csv",
    },
    {
      contents: writeCustomFieldsCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/custom-fields.csv",
    },
    {
      contents: writeCommentsCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/comments.csv",
    },
    {
      contents: writeActivityCsv(snapshot),
      mediaType: "text/csv;charset=utf-8",
      path: "data/activity.csv",
    },
    {
      contents: writeAttachmentsCsv(snapshot, manifest),
      mediaType: "text/csv;charset=utf-8",
      path: "data/attachments.csv",
    },
  ];
}

export function buildPortableArchiveTextFiles(
  snapshotInput: PortableBoardSnapshotV1,
  manifestInput: SnapshotManifestV1,
): readonly PortableTextFile[] {
  const snapshot = portableBoardSnapshotV1Schema.parse(snapshotInput);
  const manifest = snapshotManifestV1Schema.parse(manifestInput);
  const structuralFiles = buildPortableStructuralFiles(snapshot, manifest);

  return [
    structuralFiles[0]!,
    {
      contents: `${JSON.stringify(manifest, null, 2)}\n`,
      mediaType: "application/json;charset=utf-8",
      path: "manifest.json",
    },
    ...structuralFiles.slice(1),
  ];
}
